class VoiceStreamingService {
  constructor() {
    this.websocket = null;
    this.isPlaying = false;
    this.isReceivingStream = false;
    this.currentAudioElement = null;
    this.lastStreamRequest = null;
    // MediaSource streaming properties
    this.mediaSource = null;
    this.sourceBuffer = null;
    this.chunkQueue = [];
    this._onSourceOpen = null;
    // Race condition protection
    this.streamId = null;
    this.pendingChunks = [];
    // Reconnection management
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.authToken = null;
  }

  async connect(authToken) {
    // Don't reconnect if already connected
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return Promise.resolve();
    }
    
    // Validate token before connecting
    if (!authToken || authToken.split('.').length !== 3) {
      throw new Error('Invalid authentication token format');
    }
    
    // Check token expiration
    try {
      const payload = JSON.parse(atob(authToken.split('.')[1]));
      if (payload.exp * 1000 < Date.now()) {
        throw new Error('Authentication token expired');
      }
    } catch (e) {
      throw new Error('Invalid authentication token');
    }
    
    const baseUrl = process.env.REACT_APP_WEBSOCKET_URL || 'wss://stream.remote-assistant.ai';
    const wsUrl = `${baseUrl}?token=${authToken}`;
    
    return new Promise((resolve, reject) => {
      this.websocket = new WebSocket(wsUrl);
      
      this.websocket.onopen = () => {
        console.log('WebSocket connected for voice streaming');
        this.reconnectAttempts = 0; // Reset reconnect counter
        resolve();
      };
      
      this.websocket.onerror = (error) => {
        console.error('WebSocket error:', error);
        reject(error);
      };
      
      this.websocket.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          this.handleMessage(parsed);
        } catch (e) {
          console.error('JSON parse error:', e);
        }
      };
      
      this.websocket.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        this.handleDisconnection(authToken);
      };
    });
  }

  async streamVoicePreview(provider, voiceConfig, text = "Hello, this is a voice preview.") {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }

    // Validate inputs
    if (!provider || !voiceConfig) {
      throw new Error('Provider and voice configuration required');
    }
    
    if (text.length > 500) {
      throw new Error('Text too long (max 500 characters)');
    }
    
    // 1. Generate unique stream ID for race condition protection
    this.streamId = Date.now() + Math.random();
    
    // 2. FULLY CLEAN UP any previous stream and its state
    this.cleanup();
    
    // 3. Send the request to the server
    const message = {
      action: 'stream_voice',
      provider,
      voice_config: voiceConfig,
      text: text.replace(/[<>"'&]/g, ''),
      stream_id: this.streamId
    };
    
    this.websocket.send(JSON.stringify(message));
  }

  handleMessage(message) {
    // Validate message structure - handle both 'type' and 'action' fields
    if (!message || typeof message !== 'object' || (!message.type && !message.action)) {
      console.error('Invalid message format');
      return;
    }
    
    // Check if this message belongs to current stream (race condition protection)
    if (message.stream_id && message.stream_id !== this.streamId) {
      console.log('Ignoring message from old stream:', message.stream_id);
      return;
    }
    
    // Handle backend-initiated TTS streaming
    if (message.action === 'stream_voice') {
      if (!this.isReceivingStream) {
        this.stopCurrentAudio();
        this.resetAudioState();
      }
      this.handleBackendTTSRequest(message);
      return;
    }
    
    switch (message.type) {
      case 'stream_start':
        if (!this.isReceivingStream) {
          this.stopCurrentAudio();
          this.resetAudioState();
        }
        this.isReceivingStream = true;
        // Don't start MediaSource here - wait to see if it's PCM or MP3
        break;
        
      case 'audio_chunk':
        if (message.format === 'pcm') {
          this.handlePCMChunk(message);
        } else if (message.data && typeof message.data === 'string') {
          // MP3 format - start MediaSource if not already started
          if (!this.isPlaying) {
            this.startStreamingPlayback();
          }
          this.handleAudioChunk(message.data);
        } else {
          console.error('Invalid audio chunk data');
        }
        break;
        
      case 'stream_complete':
        this.isReceivingStream = false;
        
        // Process any pending chunks first
        this.processPendingChunks();
        
        // Finalize MediaSource when stream completes
        if (this.isPlaying && this.mediaSource && this.mediaSource.readyState === 'open') {
          if (this.sourceBuffer && !this.sourceBuffer.updating && this.chunkQueue.length === 0) {
            try {
              this.mediaSource.endOfStream();
            } catch (error) {
              console.warn('Failed to end MediaSource stream:', error);
            }
          }
        }
        break;
        
      case 'error':
        console.error('Streaming error:', message.message);
        this.isReceivingStream = false;
        this.stopCurrentAudio();
        this.resetAudioState();
        break;
        
      default:
        console.warn('Unknown message type:', message.type, message);
    }
  }

  async handleAudioChunk(chunkData) {
    try {
      // Handle both old MP3 format and new PCM format
      if (typeof chunkData === 'string') {
        // Old format - base64 encoded MP3
        const binaryData = atob(chunkData);
        const bytes = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          bytes[i] = binaryData.charCodeAt(i);
        }
        this.addAudioChunk(bytes);
      } else if (chunkData.format === 'pcm') {
        // New format - PCM audio data
        await this.handlePCMChunk(chunkData);
      } else {
        console.error('Unknown audio chunk format:', chunkData);
      }
    } catch (error) {
      console.error('Error processing audio chunk:', error);
    }
  }

  async handlePCMChunk(chunkData) {
    try {
      // Initialize AudioContext if needed
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.pcmBuffers = [];
        this.nextStartTime = 0;
        this.isPlaying = true; // Mark as playing for PCM
      }

      // Decode base64 PCM data
      const binaryData = atob(chunkData.data);
      const pcmData = new Int16Array(binaryData.length / 2);
      
      for (let i = 0; i < pcmData.length; i++) {
        const byte1 = binaryData.charCodeAt(i * 2);
        const byte2 = binaryData.charCodeAt(i * 2 + 1);
        pcmData[i] = (byte2 << 8) | byte1; // Little-endian 16-bit
      }

      // Convert to float32 for Web Audio API
      const floatData = new Float32Array(pcmData.length);
      for (let i = 0; i < pcmData.length; i++) {
        floatData[i] = pcmData[i] / 32768.0; // Convert to -1.0 to 1.0 range
      }

      // Create AudioBuffer
      const audioBuffer = this.audioContext.createBuffer(
        chunkData.channels || 1,
        floatData.length,
        chunkData.sample_rate || 22050
      );
      
      audioBuffer.getChannelData(0).set(floatData);

      // Play immediately
      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.audioContext.destination);
      
      const currentTime = this.audioContext.currentTime;
      const startTime = Math.max(currentTime, this.nextStartTime);
      
      source.start(startTime);
      this.nextStartTime = startTime + audioBuffer.duration;

    } catch (error) {
      console.error('Error processing PCM chunk:', error);
    }
  }

  async handleBackendTTSRequest(message) {
    try {
      const { provider, voice_config, text, stream_id } = message;
      
      if (!provider || !voice_config || !text) {
        console.error('Missing TTS parameters:', message);
        return;
      }
      
      // Update stream ID if provided
      if (stream_id) {
        this.streamId = stream_id;
      }
      
      this.lastStreamRequest = { provider, voice_config, text, stream_id };
      
      setTimeout(() => {
        if (this.lastStreamRequest && this.lastStreamRequest.stream_id === this.streamId && this.chunkQueue.length === 0 && !this.isPlaying) {
          this.fallbackToHttpAPI(this.lastStreamRequest);
        }
      }, 3000);
      
    } catch (error) {
      console.error('Backend TTS processing failed:', error);
      this.stopCurrentAudio();
      this.resetAudioState();
    }
  }

  disconnect() {
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnection
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    this.cleanup();
  }

  // New method: Clear audio without disconnecting WebSocket
  clearAudio() {
    console.log('🧹 Clearing audio buffers (keeping WebSocket connected)');
    this.stopCurrentAudio();
    this.resetAudioState();
    this.forceCleanupMediaSource();
    this.streamId = null;
    this.lastStreamRequest = null;
    // Clean up AudioContext for PCM
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
      this.pcmBuffers = [];
      this.nextStartTime = 0;
    }
  }

  // Handle unexpected disconnections with auto-reconnect
  async handleDisconnection(authToken) {
    this.cleanup();
    
    // Don't reconnect if we've exceeded max attempts or if disconnect was intentional
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnection attempts reached');
      return;
    }
    
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff
    
    console.log(`WebSocket reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    setTimeout(async () => {
      try {
        await this.connect(authToken || this.authToken);
        console.log('WebSocket reconnected successfully');
      } catch (error) {
        console.error('WebSocket reconnection failed:', error);
      }
    }, delay);
  }

  // Store auth token for reconnection
  setAuthToken(token) {
    this.authToken = token;
  }

  stopCurrentAudio() {
    // Stop HTML5 audio element
    if (this.currentAudioElement) {
      this.currentAudioElement.pause();
      this.currentAudioElement.currentTime = 0;
      this.currentAudioElement = null;
    }
  }
  
  startStreamingPlayback() {
    if (this.isPlaying) {
      return;
    }
    
    // Force complete cleanup before creating new MediaSource
    this.forceCleanupMediaSource();
    
    this.isPlaying = true;
    
    this.currentAudioElement = new Audio();
    this.mediaSource = new MediaSource();
    this.chunkQueue = [];
    
    this._onSourceOpen = () => {
      // Check if this MediaSource is still current (race condition protection)
      if (this.mediaSource?.readyState !== 'open') {
        console.warn('⚠️ MediaSource was closed before it could be used. This is normal during rapid restarts.');
        return;
      }
      
      try {
        this.sourceBuffer = this.mediaSource.addSourceBuffer('audio/mpeg');
        
        this.sourceBuffer.addEventListener('updateend', () => {
          this.processChunkQueue();
        });
        
        // Process any pending chunks that arrived before SourceBuffer was ready
        this.processPendingChunks();
        this.processChunkQueue();
        
      } catch (error) {
        console.error('Failed to create SourceBuffer:', error);
        this.isPlaying = false;
      }
    };
    
    this.mediaSource.addEventListener('sourceopen', this._onSourceOpen);
    
    this.currentAudioElement.src = URL.createObjectURL(this.mediaSource);
    
    this.currentAudioElement.onended = () => {
      this.isPlaying = false;
    };
    
    this.currentAudioElement.onerror = (error) => {
      console.error('Streaming audio error:', error);
      this.isPlaying = false;
    };
    
    this.currentAudioElement.play().catch(error => {
      console.error('Audio play failed:', error);
      this.isPlaying = false;
    });
  }
  
  addAudioChunk(chunk) {
    if (!this.isPlaying) {
      return;
    }
    
    // If SourceBuffer not ready, store in pending chunks
    if (!this.sourceBuffer) {
      this.pendingChunks.push(chunk);
      return;
    }
    
    // Add chunk to queue
    this.chunkQueue.push(chunk);
    
    // Process queue if SourceBuffer is ready
    if (!this.sourceBuffer.updating) {
      this.processChunkQueue();
    }
  }
  
  processPendingChunks() {
    // Move pending chunks to main queue when SourceBuffer is ready
    while (this.pendingChunks.length > 0) {
      this.chunkQueue.push(this.pendingChunks.shift());
    }
  }
  
  processChunkQueue() {
    // Only process if SourceBuffer is ready
    if (!this.sourceBuffer || this.sourceBuffer.updating || this.chunkQueue.length === 0) {
      return;
    }
    
    const chunk = this.chunkQueue.shift();
    
    try {
      this.sourceBuffer.appendBuffer(chunk);
    } catch (error) {
      console.error('Failed to append buffer:', error);
      this.chunkQueue.unshift(chunk);
    }
  }

  resetAudioState() {
    this.isPlaying = false;
    this.isReceivingStream = false;
    this.pendingChunks = [];
    this.chunkQueue = [];
  }

  forceCleanupMediaSource() {
    // Immediately revoke any existing object URL
    if (this.currentAudioElement && this.currentAudioElement.src) {
      URL.revokeObjectURL(this.currentAudioElement.src);
    }
    
    // Force close MediaSource
    if (this.mediaSource) {
      try {
        if (this.mediaSource.readyState === 'open') {
          this.mediaSource.endOfStream();
        }
      } catch (e) {
        // Ignore errors during cleanup
      }
      
      // Remove event listener to prevent race conditions
      if (this._onSourceOpen) {
        this.mediaSource.removeEventListener('sourceopen', this._onSourceOpen);
      }
      
      this.mediaSource = null;
    }
    
    this.sourceBuffer = null;
    this.chunkQueue = [];
    this.pendingChunks = [];
  }

  cleanup() {
    // Stop any current audio
    this.stopCurrentAudio();
    
    // Reset audio state
    this.resetAudioState();
    
    // Force complete MediaSource cleanup
    this.forceCleanupMediaSource();
    
    // Clean up AudioContext for PCM
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
      this.pcmBuffers = [];
      this.nextStartTime = 0;
    }
    
    // Clear stream tracking
    this.streamId = null;
    this.lastStreamRequest = null;
    
    // Remove event listener
    if (this._onSourceOpen) {
      this._onSourceOpen = null;
    }
  }

  async fallbackToHttpAPI(requestParams) {
    try {
      const { default: configService } = await import('./configService');
      
      const response = await configService.generateVoicePreview(
        requestParams.provider,
        requestParams.voice_config,
        requestParams.text
      );
      
      if (response.success && response.audio_url) {
        const audio = new Audio(response.audio_url);
        this.currentAudioElement = audio;
        
        audio.onended = () => {
          this.isPlaying = false;
          this.currentAudioElement = null;
        };
        
        audio.onerror = (error) => {
          console.error('HTTP fallback audio error:', error);
          this.isPlaying = false;
          this.currentAudioElement = null;
        };
        
        this.isPlaying = true;
        await audio.play();
        
      } else {
        console.error('HTTP fallback failed:', response);
      }
    } catch (error) {
      console.error('HTTP fallback error:', error);
    } finally {
      this.lastStreamRequest = null;
    }
  }


}

const voiceStreamingService = new VoiceStreamingService();
export default voiceStreamingService;